import { Chip } from "@heroui/chip";

import { ClaimActions } from "./ClaimActions";

import { DateDisplay } from "@/components/ui/DateDisplay";
import { getVolunteerFullName } from "@/lib/utils/volunteer";

const CLAIM_STATUS_COLORS: Record<string, "success" | "primary" | "default"> = {
  claimed: "primary",
  completed: "success",
};

const NEED_STATUS_COLORS: Record<
  string,
  "success" | "primary" | "warning" | "default"
> = {
  claimed: "primary",
  completed: "success",
  open: "warning",
};

const URGENCY_COLORS: Record<string, "danger" | "warning" | "default"> = {
  high: "danger",
  medium: "warning",
};

function getClaimStatusColor(
  status: string,
): "success" | "primary" | "default" {
  return CLAIM_STATUS_COLORS[status] ?? "default";
}

function getNeedStatusColor(
  status: string,
): "success" | "primary" | "warning" | "default" {
  return NEED_STATUS_COLORS[status] ?? "default";
}

function getUrgencyColor(urgency: string): "danger" | "warning" | "default" {
  return URGENCY_COLORS[urgency] ?? "default";
}

function NeedDetailsSection({
  claim,
}: {
  claim: {
    need: {
      status: string;
      urgency: string;
      area: { name: string };
    };
    volunteer: { firstName: string; lastName: string; email: string };
  };
}) {
  return (
    <>
      <p>
        <span className="font-medium">Volunteer:</span>{" "}
        {getVolunteerFullName(claim.volunteer)}
      </p>
      <p>
        <span className="font-medium">Area:</span> {claim.need.area.name}
      </p>
      <p>
        <span className="font-medium">Need Status:</span>{" "}
        <Chip
          color={getNeedStatusColor(claim.need.status)}
          size="sm"
          variant="flat"
        >
          {claim.need.status}
        </Chip>
      </p>
      <p>
        <span className="font-medium">Urgency:</span>{" "}
        <Chip
          color={getUrgencyColor(claim.need.urgency)}
          size="sm"
          variant="flat"
        >
          {claim.need.urgency}
        </Chip>
      </p>
    </>
  );
}

function VolunteerDetailsSection({
  volunteer,
}: {
  volunteer: {
    email: string;
    phone?: string | null;
  };
}) {
  return (
    <>
      <p>Email: {volunteer.email}</p>
      {volunteer.phone && <p>Phone: {volunteer.phone}</p>}
    </>
  );
}

interface ClaimDetailProps {
  claim: {
    id: string;
    claimToken: string;
    status: string;
    quantity: number;
    claimedAt: Date;
    unclaimedAt?: Date | null;
    volunteer: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string | null;
    };
    need?: {
      id: string;
      title: string;
      status: string;
      urgency: string;
      area: {
        name: string;
      };
    };
  };
  onMarkCompleted?: (claimToken: string) => Promise<void>;
  onUnclaim?: (claimToken: string) => Promise<void>;
  showNeedDetails?: boolean;
  showActions?: boolean;
  className?: string;
  isLoading?: boolean;
}

function ClaimHeader({
  claim,
  showNeedDetails,
}: {
  claim: ClaimDetailProps["claim"];
  showNeedDetails: boolean;
}) {
  const title =
    showNeedDetails && claim.need
      ? claim.need.title
      : getVolunteerFullName(claim.volunteer);

  return (
    <div className="flex items-center gap-2 mb-2">
      <h4 className="font-medium">{title}</h4>
      <Chip color={getClaimStatusColor(claim.status)} size="sm" variant="flat">
        {claim.status}
      </Chip>
    </div>
  );
}

function ClaimDates({ claim }: { claim: ClaimDetailProps["claim"] }) {
  return (
    <>
      <p>
        Quantity: {claim.quantity} item{claim.quantity !== 1 ? "s" : ""}
      </p>
      <p>
        Claimed:{" "}
        <DateDisplay
          ariaLabel={`Claimed on: ${new Date(claim.claimedAt).toLocaleDateString()}`}
          date={claim.claimedAt}
        />
      </p>
      {claim.unclaimedAt && (
        <p>
          Unclaimed:{" "}
          <DateDisplay
            ariaLabel={`Unclaimed on: ${new Date(claim.unclaimedAt).toLocaleDateString()}`}
            date={claim.unclaimedAt}
          />
        </p>
      )}
    </>
  );
}

function ClaimDetailsSection({
  claim,
  showNeedDetails,
}: {
  claim: ClaimDetailProps["claim"];
  showNeedDetails: boolean;
}) {
  return (
    <div className="text-sm text-default-500 space-y-1">
      {showNeedDetails && claim.need ? (
        <NeedDetailsSection
          claim={{ need: claim.need, volunteer: claim.volunteer }}
        />
      ) : (
        <VolunteerDetailsSection volunteer={claim.volunteer} />
      )}
      <ClaimDates claim={claim} />
    </div>
  );
}

export function ClaimDetail({
  claim,
  className = "",
  isLoading = false,
  onMarkCompleted,
  onUnclaim,
  showActions = true,
  showNeedDetails = false,
}: ClaimDetailProps) {
  return (
    <div className={`border border-default-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <ClaimHeader claim={claim} showNeedDetails={showNeedDetails} />
          <ClaimDetailsSection
            claim={claim}
            showNeedDetails={showNeedDetails}
          />
        </div>

        {showActions && (
          <ClaimActions
            claimToken={claim.claimToken}
            isLoading={isLoading}
            status={claim.status}
            onMarkCompleted={onMarkCompleted}
            onUnclaim={onUnclaim}
          />
        )}
      </div>
    </div>
  );
}
